/** Duração em minutos para Filas VTME (SLA e elapsed). */

export type DurationParts = {
  days: number;
  hours: number;
  minutes: number;
};

export type DurationUnit = 'days' | 'hours' | 'minutes';

export const DURATION_UNIT_OPTIONS: { value: DurationUnit; text: string }[] = [
  { value: 'days', text: 'Dias' },
  { value: 'hours', text: 'Horas' },
  { value: 'minutes', text: 'Minutos' },
];

/** ≤0.5 para baixo; ≥0.6 para cima (ex.: 30.5→30, 30.6→31). */
export function roundDurationMinutes(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const floor = Math.floor(value);
  const frac = value - floor;
  if (frac <= 0.5) return floor;
  return floor + 1;
}

export function splitDurationMinutes(totalMinutes: number): DurationParts {
  const total = Math.max(0, roundDurationMinutes(totalMinutes));
  const days = Math.floor(total / 1440);
  const rem = total % 1440;
  const hours = Math.floor(rem / 60);
  const minutes = rem % 60;
  return { days, hours, minutes };
}

export function combineDurationMinutes(parts: DurationParts): number {
  const days = Math.max(0, Math.floor(parts.days) || 0);
  const hours = Math.min(23, Math.max(0, Math.floor(parts.hours) || 0));
  const minutes = Math.min(59, Math.max(0, Math.floor(parts.minutes) || 0));
  return days * 1440 + hours * 60 + minutes;
}

/** Escolhe a unidade mais “limpa” para o total em minutos. */
export function pickDurationUnit(totalMinutes: number): DurationUnit {
  const total = Math.max(1, roundDurationMinutes(totalMinutes) || 1);
  if (total % 1440 === 0) return 'days';
  if (total % 60 === 0) return 'hours';
  return 'minutes';
}

export function durationValueFromMinutes(totalMinutes: number, unit: DurationUnit): number {
  const total = Math.max(1, roundDurationMinutes(totalMinutes) || 1);
  if (unit === 'days') return Math.max(1, Math.round(total / 1440) || 1);
  if (unit === 'hours') return Math.max(1, Math.round(total / 60) || 1);
  return total;
}

export function durationValueToMinutes(value: number, unit: DurationUnit): number {
  const n = Math.max(1, Math.floor(value) || 1);
  if (unit === 'days') return n * 1440;
  if (unit === 'hours') return n * 60;
  return n;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

const dayLabel = (days: number) => (days === 1 ? '1 dia' : `${days} dias`);

/**
 * Exemplos:
 * - 30 → `30min`
 * - 60 → `1h`
 * - 61 → `01:01h`
 * - 17d → `17 dias`
 * - 17d + 10min → `17 dias 10min`
 * - 17d + 4h → `17 dias 04h`
 * - 17d + 4h08 → `17 dias 04:08h`
 */
export function formatDurationFromMinutes(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const total = roundDurationMinutes(value);
  if (total < 60) return `${total}min`;

  const { days, hours, minutes } = splitDurationMinutes(total);

  if (days <= 0) {
    if (minutes === 0) return `${hours}h`;
    return `${pad2(hours)}:${pad2(minutes)}h`;
  }

  const label = dayLabel(days);
  if (hours === 0 && minutes === 0) return label;
  if (hours === 0) return `${label} ${minutes}min`;
  if (minutes === 0) return `${label} ${pad2(hours)}h`;
  return `${label} ${pad2(hours)}:${pad2(minutes)}h`;
}
